import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { server } from "../../server";
import axios from "axios";

const DownloadAnnualResultXLS = async ({
  marks,
  heads,
  attendanceData,
  selectedClass,
  selectedSection,
  workingDays,
  holidays = [],
}) => {
  const subjectOrder = [
    "English",
    "Hindi",
    "Maths",
    "EVS",
    "Science",
    "SST",
    "Sanskrit",
    "GK",
    "AI",
  ];

  const halfYearlyConfig = heads[0].examHeads.find(
    (e) => e.examType.toLowerCase() === "halfyearly",
  );
  const annualConfig = heads[0].examHeads.find(
    (e) => e.examType.toLowerCase() === "annual",
  );

  const halfYearlyHeadLabels = halfYearlyConfig.heads;
  const annualHeadLabels = annualConfig ? annualConfig.heads : [];

  const calcExamMaxTotal = (headLabels) => {
    let total = 0;
    headLabels.forEach((h) => {
      if (h.label.toLowerCase() === "weekly test 1") total += 10;
      else if (h.label.toLowerCase() !== "weekly test 2") total += h.max;
    });
    return total;
  };

  const hyMaxTotal = calcExamMaxTotal(halfYearlyHeadLabels);
  const annMaxTotal = calcExamMaxTotal(annualHeadLabels);

  const getGrade = (percentage) => {
    if (percentage >= 91) return "A1";
    if (percentage >= 81) return "A2";
    if (percentage >= 71) return "B1";
    if (percentage >= 61) return "B2";
    if (percentage >= 51) return "C1";
    if (percentage >= 41) return "C2";
    if (percentage >= 33) return "D";
    return "E";
  };

  const normalize = (str) => str?.trim().toLowerCase();

  // Visible heads (skip weekly test 2)
  const visibleHyHeads = halfYearlyHeadLabels.filter(
    (h) => h.label.toLowerCase() !== "weekly test 2",
  );
  const visibleAnnHeads = annualHeadLabels.filter(
    (h) => h.label.toLowerCase() !== "weekly test 2",
  );

  const labelForHead = (h) =>
    h.label.toLowerCase() === "weekly test 1"
      ? "Weekly Test (10)"
      : `${h.label} (${h.max})`;

  // ── Fetch all students ────────────────────────────────────────────────
  const studentLookup = {};
  try {
    const bulkRes = await axios.post(
      `${server}/studentdata_using_class_and_section_mongodb`,
      { class: selectedClass, section: selectedSection },
      {
        headers: {
          Authorization: `${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      },
    );
    const bulkData = bulkRes.data?.["body-json"]?.body?.students || [];
    bulkData.forEach((s) => {
      if (s.admission_no) studentLookup[s.admission_no] = s;
    });
  } catch (err) {
    console.error("Bulk student fetch failed:", err);
  }

  const enrichedMarks = marks.map((student) => {
    const studentData = studentLookup[student.admission_no];
    return studentData ? { ...student, ...studentData } : student;
  });

  enrichedMarks.sort((a, b) => {
    const rollA = Number(a?.studentDetails?.rollNo) || Infinity;
    const rollB = Number(b?.studentDetails?.rollNo) || Infinity;
    return rollA - rollB;
  });

  // ── Build header ──────────────────────────────────────────────────────
  const infoColumns = [
    "Roll No",
    "Admission No",
    "Student Name",
    "Father Name",
    "Mother Name",
    "Class",
    "Section",
    "DOB",
    "Days Present",
    "Working Days",
    "Attendance %",
  ];

  // Per-subject: HY heads + HY Total + HY Grade | Annual heads + Annual Total | Avg + Final Grade
  const subjectColumns = [];
  subjectOrder.forEach((subject) => {
    visibleHyHeads.forEach((h) => {
      subjectColumns.push(`${subject} - HY ${labelForHead(h)}`);
    });
    subjectColumns.push(`${subject} - HY Total (${hyMaxTotal})`);
    subjectColumns.push(`${subject} - HY Grade`);

    visibleAnnHeads.forEach((h) => {
      subjectColumns.push(`${subject} - Annual ${labelForHead(h)}`);
    });
    subjectColumns.push(`${subject} - Annual Total (${annMaxTotal})`);

    subjectColumns.push(`${subject} - Avg (HY+Annual)`);
    subjectColumns.push(`${subject} - Final Grade`);
  });

  const summaryColumns = [
    "HY Grand Total",
    "Annual Grand Total",
    "Overall Percentage (%)",
    "Overall Grade",
  ];

  const headerRow = [...infoColumns, ...subjectColumns, ...summaryColumns];

  // ── Helper: extract marks for one exam ───────────────────────────────
  const buildExamValues = (visibleHeads, marksByHead, fallbackMax) => {
    let obtained = 0;
    let maxTotal = 0;
    const cells = [];

    visibleHeads.forEach((h) => {
      if (h.label.toLowerCase() === "weekly test 1") {
        const s1 = Number(marksByHead["h1"]) || 0;
        const s2 = Number(marksByHead["h2"]) || 0;
        const nullBoth =
          (marksByHead["h1"] === null || marksByHead["h1"] === 0) &&
          (marksByHead["h2"] === null || marksByHead["h2"] === 0);
        maxTotal += 10;
        if (!nullBoth) {
          const combined = Math.round((s1 + s2) / 4);
          obtained += combined;
          cells.push(combined);
        } else {
          cells.push("AB");
        }
      } else {
        const score = marksByHead[h.id] ?? null;
        maxTotal += h.max;
        if (score === null || score === 0) {
          cells.push("AB");
        } else {
          obtained += score;
          cells.push(score);
        }
      }
    });

    return { cells, obtained, maxTotal: maxTotal || fallbackMax };
  };

  // ── Build data rows ───────────────────────────────────────────────────
  const dataRows = [];

  for (const student of enrichedMarks) {
    const studentName = student?.name || "-";
    const fatherName = student?.parentDetails?.[0]?.name || "-";
    const motherName = student?.parentDetails?.[1]?.name || "-";
    const currentClass = student?.currentClass || "-";
    const section = student?.studentDetails?.currentSection || "-";
    const rollNo = student?.studentDetails?.rollNo || "-";
    const dobRaw = student?.dateOfBirth || "-";
    const dob = dobRaw !== "-" ? dobRaw.split("-").reverse().join("/") : "-";

    // Attendance (holiday-aware, deduplicated)
    const studentAttendance =
      attendanceData.find((att) => att.admission_no === student.admission_no)
        ?.attendance || [];

    const seenDates = new Set();
    let totalPresentDays = 0;
    studentAttendance.forEach((day) => {
      if (!day.Date || seenDates.has(day.Date)) return;
      seenDates.add(day.Date);
      const isHoliday = holidays.find(
        (h) => h.date === day.Date && h.class.includes(selectedClass),
      );
      if (isHoliday) return;
      if (day.SchoolStatus?.toLowerCase() === "present") totalPresentDays++;
    });

    const attendancePct =
      workingDays > 0
        ? parseFloat(((totalPresentDays / workingDays) * 100).toFixed(2))
        : 0;

    // Subjects
    const hySubjects = student.result?.["half yearly"] || [];
    const annSubjects = student.result?.["annual"] || [];

    const hySubjectMap = {};
    const annSubjectMap = {};
    hySubjects.forEach((s) => {
      hySubjectMap[normalize(s.subject)] = s;
    });
    annSubjects.forEach((s) => {
      annSubjectMap[normalize(s.subject)] = s;
    });

    let grandHyObtainedNorm = 0;
    let grandAnnObtainedNorm = 0;
    let subjectCount = 0;
    const subjectCells = [];

    subjectOrder.forEach((subjectName) => {
      const hyObj = hySubjectMap[normalize(subjectName)];
      const annObj = annSubjectMap[normalize(subjectName)];

      if (!hyObj && !annObj) {
        // Subject not present at all — fill all columns with "-"
        const totalCols =
          visibleHyHeads.length +
          2 + // HY heads + Total + Grade
          visibleAnnHeads.length +
          1 + // Annual heads + Total
          2; // Avg + Final Grade
        for (let i = 0; i < totalCols; i++) subjectCells.push("-");
        return;
      }

      // HY
      const hy = hyObj
        ? buildExamValues(visibleHyHeads, hyObj.marks || {}, hyMaxTotal)
        : {
            cells: visibleHyHeads.map(() => "-"),
            obtained: 0,
            maxTotal: hyMaxTotal,
          };

      subjectCells.push(...hy.cells);
      subjectCells.push(hyObj ? hy.obtained : "-");

      const hyPct = (hy.obtained / hy.maxTotal) * 100;
      subjectCells.push(hyObj ? getGrade(hyPct) : "-");

      // Annual
      const ann = annObj
        ? buildExamValues(visibleAnnHeads, annObj.marks || {}, annMaxTotal)
        : {
            cells: visibleAnnHeads.map(() => "-"),
            obtained: 0,
            maxTotal: annMaxTotal,
          };

      subjectCells.push(...ann.cells);
      subjectCells.push(annObj ? ann.obtained : "-");

      // Avg and final grade
      const hyNorm = (hy.obtained / hy.maxTotal) * 100;
      const annNorm = (ann.obtained / ann.maxTotal) * 100;
      const avgPct = (hyNorm + annNorm) / 2;
      const avgDisplayedPct = Math.round(avgPct);
      const avgMarks = Math.round((hy.obtained + ann.obtained) / 2);

      subjectCells.push(avgMarks);
      subjectCells.push(getGrade(avgDisplayedPct));

      grandHyObtainedNorm += hyNorm;
      grandAnnObtainedNorm += annNorm;
      subjectCount++;
    });

    const grandHyMax = subjectCount * 100;
    const grandAnnMax = subjectCount * 100;
    const grandHyDisplay = Math.round(grandHyObtainedNorm);
    const grandAnnDisplay = Math.round(grandAnnObtainedNorm);

    const overallAvgPct =
      grandHyMax > 0
        ? ((grandHyObtainedNorm / grandHyMax +
            grandAnnObtainedNorm / grandAnnMax) /
            2) *
          100
        : 0;
    const displayPercentage = Math.round(overallAvgPct);
    const overallGrade = getGrade(displayPercentage);

    dataRows.push([
      rollNo,
      student.admission_no,
      studentName,
      fatherName,
      motherName,
      currentClass,
      section,
      dob,
      totalPresentDays,
      workingDays,
      attendancePct,
      ...subjectCells,
      grandHyDisplay,
      grandAnnDisplay,
      displayPercentage,
      overallGrade,
    ]);
  }

  // ── Workbook ──────────────────────────────────────────────────────────
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);

  ws["!cols"] = headerRow.map((h) => ({
    wch: Math.max(String(h).length + 2, 12),
  }));
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };

  XLSX.utils.book_append_sheet(wb, ws, "Annual Result");

  // Grade Scale
  const gradeSheet = XLSX.utils.aoa_to_sheet([
    ["Marks Range", "Grade"],
    ["91 - 100", "A1"],
    ["81 - 90", "A2"],
    ["71 - 80", "B1"],
    ["61 - 70", "B2"],
    ["51 - 60", "C1"],
    ["41 - 50", "C2"],
    ["33 - 40", "D"],
    ["32 & Below", "E"],
  ]);
  gradeSheet["!cols"] = [{ wch: 15 }, { wch: 8 }];
  XLSX.utils.book_append_sheet(wb, gradeSheet, "Grade Scale");

  const xlsxBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([xlsxBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${selectedClass}-${selectedSection}-Annual-Result-2025.xlsx`);
};

export default DownloadAnnualResultXLS;
